import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { useCrearLineaManual } from "@/hooks/useValorizacion";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import type { Moneda } from "@/lib/informes";
import { numeroEscrito } from "@/lib/numeroEscrito";
import {
  UNIDADES_DE_LINEA,
  interpretarPrecio,
  unidadPorDefecto,
  type CuerpoDeLineaManual,
  type RubroDeValorizacion,
  type UnidadDeLinea,
} from "@/lib/valorizacion";

const MONEDAS: Moneda[] = ["UF", "CLP", "USD"];

interface FormularioDeLineaManualProps {
  informeId: number;
  rubro: RubroDeValorizacion;
  onListo: () => void;
}

type Errores = Partial<Record<"descripcion" | "cantidad" | "precio" | "general", string>>;

/**
 * Una línea que no sale del expediente («Riego tecnificado», por hectárea).
 * Se pide lo mínimo: qué es y cuánto hay; el precio puede venir después.
 */
export function FormularioDeLineaManual({ informeId, rubro, onListo }: FormularioDeLineaManualProps) {
  const crear = useCrearLineaManual(informeId);
  const [descripcion, setDescripcion] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [unidad, setUnidad] = useState<UnidadDeLinea>(unidadPorDefecto(rubro));
  const [precio, setPrecio] = useState("");
  const [moneda, setMoneda] = useState<Moneda>("UF");
  const [errores, setErrores] = useState<Errores>({});

  function enviar() {
    const nuevos: Errores = {};
    const cantidadLeida = numeroEscrito(cantidad);
    const precioLeido = interpretarPrecio(precio, moneda);

    if (descripcion.trim() === "") nuevos.descripcion = "Describe la línea.";
    if (cantidad.trim() === "") nuevos.cantidad = "Indica la cantidad.";
    else if (cantidadLeida === null || cantidadLeida < 0) nuevos.cantidad = "La cantidad debe ser un número, sin negativos.";
    if (precioLeido.estado === "invalido") nuevos.precio = precioLeido.mensaje;

    setErrores(nuevos);
    if (Object.keys(nuevos).length > 0 || cantidadLeida === null) return;

    const cuerpo: CuerpoDeLineaManual = {
      rubro,
      descripcion: descripcion.trim(),
      cantidad: cantidadLeida,
      unidad,
    };

    if (precioLeido.estado === "ok") {
      cuerpo.precio_unitario = precioLeido.valor;
      cuerpo.moneda = moneda;
    }

    crear.mutate(cuerpo, {
      onSuccess: onListo,
      onError: (falla) => {
        const campos = parseValidationErrors(falla);
        setErrores({
          descripcion: campos?.descripcion,
          cantidad: campos?.cantidad,
          precio: campos?.precio_unitario,
          general: campos ? undefined : getApiErrorMessage(falla, "No pudimos crear la línea. Inténtalo de nuevo."),
        });
      },
    });
  }

  return (
    <form
      noValidate
      className="bg-superficie-sutil grid gap-3 rounded-xl border p-3 sm:grid-cols-2"
      onSubmit={(evento) => {
        evento.preventDefault();
        enviar();
      }}
    >
      <Campo id={`manual-${rubro}-descripcion`} etiqueta="Descripción de la línea" error={errores.descripcion} ancho>
        <Input
          id={`manual-${rubro}-descripcion`}
          value={descripcion}
          aria-invalid={errores.descripcion !== undefined}
          onChange={(evento) => setDescripcion(evento.target.value)}
        />
      </Campo>

      <Campo id={`manual-${rubro}-cantidad`} etiqueta="Cantidad de la línea" error={errores.cantidad}>
        <Input
          id={`manual-${rubro}-cantidad`}
          inputMode="decimal"
          className="font-mono"
          value={cantidad}
          aria-invalid={errores.cantidad !== undefined}
          onChange={(evento) => setCantidad(evento.target.value)}
        />
      </Campo>

      <Campo id={`manual-${rubro}-unidad`} etiqueta="Unidad de la línea">
        <NativeSelect id={`manual-${rubro}-unidad`} value={unidad} onChange={(evento) => setUnidad(evento.target.value as UnidadDeLinea)}>
          {UNIDADES_DE_LINEA.map((u) => (
            <NativeSelectOption key={u} value={u}>
              {u}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Campo>

      <Campo id={`manual-${rubro}-precio`} etiqueta="Precio unitario de la línea (opcional)" error={errores.precio}>
        <Input
          id={`manual-${rubro}-precio`}
          inputMode="decimal"
          className="font-mono"
          value={precio}
          aria-invalid={errores.precio !== undefined}
          onChange={(evento) => setPrecio(evento.target.value)}
        />
      </Campo>

      <Campo id={`manual-${rubro}-moneda`} etiqueta="Moneda de la línea nueva">
        <NativeSelect id={`manual-${rubro}-moneda`} value={moneda} onChange={(evento) => setMoneda(evento.target.value as Moneda)}>
          {MONEDAS.map((m) => (
            <NativeSelectOption key={m} value={m}>
              {m}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Campo>

      {errores.general ? (
        <p role="alert" className="text-label text-danger-soft-foreground sm:col-span-2">
          {errores.general}
        </p>
      ) : null}

      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" disabled={crear.isPending}>
          {crear.isPending ? "Creando…" : "Crear línea"}
        </Button>
        <Button type="button" variant="ghost" onClick={onListo}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

function Campo({
  id,
  etiqueta,
  error,
  ancho,
  children,
}: {
  id: string;
  etiqueta: string;
  error?: string;
  ancho?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={ancho ? "flex flex-col gap-1.5 sm:col-span-2" : "flex flex-col gap-1.5"}>
      <Label htmlFor={id}>{etiqueta}</Label>
      {children}
      {error ? (
        <p role="alert" className="text-caption text-danger-soft-foreground">
          {error}
        </p>
      ) : null}
    </div>
  );
}
